import { Component, OnInit, AfterViewInit, Inject, Optional, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';

import { PropertyService } from '../../services/property.service';
import { NavigationContextService } from '../../services/navigation-context.service';
import { StartLeaseComponent } from '../start-lease/start-lease.component';
import { ModifyLeaseComponent } from '../modify-lease/modify-lease.component';
import { ViewLeaseDialogComponent } from '../view-lease-dialog/view-lease-dialog.component';

@Component({
  selector: 'app-manage-leases',
  templateUrl: './manage-leases.component.html',
  styleUrls: ['./manage-leases.component.scss']
})
export class ManageLeasesComponent implements OnInit, AfterViewInit {
  leases: any[] = [];
  allLeases: any[] = [];
  filteredLeases: any[] = [];
  property: any;
  loading = false;
  activeLease: any = null;
  allDisplayedColumns: string[] = ['property', 'tenant', 'startDate', 'endDate', 'monthlyCost', 'status', 'actions'];
  propertyDisplayedColumns: string[] = ['tenant', 'startDate', 'endDate', 'monthlyCost', 'status', 'actions'];
  get displayedColumns(): string[] {
    return this.property ? this.propertyDisplayedColumns : this.allDisplayedColumns;
  }
  dataSource = new MatTableDataSource<any>();
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  
  // Pagination properties
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  
  // Filter properties
  allProperties: any[] = [];
  selectedPropertyId: string | null = null;
  selectedStatus: string | null = null; // 'ACTIVE', 'EXPIRED', or null for all
  searchText: string = '';
  selectedDateFilter: string = 'all'; // 'all', 'thisMonth', 'lastMonth', 'custom'
  customStartDate: string = '';
  customEndDate: string = '';

  constructor(private propertyService: PropertyService,
              @Optional() public dialogRef: MatDialogRef<ManageLeasesComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
              private route: ActivatedRoute,
              private router: Router,
              private dialog: MatDialog,
              private navContext: NavigationContextService) { }

  ngOnInit() {
    const propertyId = this.route.snapshot.queryParamMap.get('propertyId');
    if (this.data && this.data.property) {
      this.property = this.data.property;
      this.refresh();
    } else if (propertyId) {
      this.loading = true;
      this.propertyService.getPublicProperty(propertyId).then(p => {
        this.property = p;
        this.refresh();
      }).catch(() => {
        this.loading = false;
        this.property = null;
      });
    } else {
      this.loadAllLeases();
    }
  }

  ngAfterViewInit() {
    if (this.sort && this.dataSource) {
      this.dataSource.sort = this.sort;
    }
    // Explicitly ensure paginator is NOT connected to dataSource
    if (this.dataSource.paginator) {
      this.dataSource.paginator = null;
    }
    if (this.paginator) {
      // For server-side pagination, we handle pagination events manually
      this.paginator.page.subscribe(event => {
        this.onPageChange(event);
      });
    }
  }
  
  onPageChange(event: any) {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadAllLeases();
  }

  loadAllLeases() {
    this.loading = true;
    const dateRange = this.getDateRange();
    Promise.all([
      this.propertyService.getAllLeases(
        this.currentPage,
        this.pageSize,
        this.selectedPropertyId || undefined,
        undefined,
        dateRange.startDate,
        dateRange.endDate
      ),
      this.propertyService.getProperties()
    ]).then(([response, properties]: [any, any[]]) => {
      this.loading = false;
      
      // Handle paginated response
      if (response && response.data && Array.isArray(response.data)) {
        // IMPORTANT: Replace allLeases with ONLY the current page's data
        this.allLeases = response.data;
        this.totalItems = response.total || 0;
        this.currentPage = response.page || 1;
        this.pageSize = response.pageSize || 10;
        console.log(`[ManageLeases] Loaded page ${this.currentPage}, showing ${this.allLeases.length} of ${this.totalItems} total leases`);
      } else {
        // Fallback for non-paginated response
        this.allLeases = Array.isArray(response) ? response : [];
        this.totalItems = this.allLeases.length;
        console.warn('[ManageLeases] Received non-paginated response');
      }
      
      this.allProperties = Array.isArray(properties) ? properties : [];
      // Apply client-side search filter on current page data
      this.applyAllFilters();
    }).catch((error) => {
      console.error('Error loading leases:', error);
      this.loading = false;
      this.allLeases = [];
      this.allProperties = [];
      this.totalItems = 0;
    });
  }

  getDateRange(): { startDate?: string, endDate?: string } {
    if (this.selectedDateFilter === 'all') {
      return {};
    }
    return {
      startDate: this.customStartDate || undefined,
      endDate: this.customEndDate || undefined
    };
  }

  refresh() {
    if (this.property) {
      this.loading = true;
      this.propertyService.getLeases(this.property).then((leases: any[]) => {
        this.leases = leases || [];
        this.dataSource.data = this.leases;
        this.totalItems = this.leases.length;
        this.loading = false;
        this.propertyService.getActiveLease(this.property).then((lease) => {
          this.activeLease = lease;
        }).catch(() => {
          this.activeLease = null;
        });
      }).catch(() => {
        this.leases = [];
        this.dataSource.data = [];
        this.totalItems = 0;
        this.loading = false;
      });
    } else {
      this.loadAllLeases();
    }
  }

  startLease() {
    if (!this.property || !this.property.id) {
      return;
    }

    this.router.navigate(['/start-lease', this.property.id]);
  }

  onRowClick(event: MouseEvent, lease: any) {
    // Don't navigate if clicking on a button or inside a button
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.tagName === 'BUTTON' || target.closest('mat-icon')) {
      return;
    }
    this.viewLeaseSummary(lease);
  }

  /** Open the nice read-only lease summary (with document preview / generate). */
  viewLeaseSummary(lease: any, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    if (!lease || !lease.id) {
      alert('Cannot view lease: lease ID not found');
      return;
    }
    const property = lease.property || this.property || { id: lease.propertyId };
    if (!property || !property.id) {
      alert('Cannot view lease: property not found');
      return;
    }
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    const dialogRef = this.dialog.open(ViewLeaseDialogComponent, {
      width: isMobile ? '100vw' : '960px',
      maxWidth: '100vw',
      height: isMobile ? '100%' : undefined,
      maxHeight: '100vh',
      autoFocus: false,
      panelClass: 'lease-dialog-panel',
      data: { lease, property }
    });
    dialogRef.afterClosed().subscribe(() => {
      if (this.property) {
        this.refresh();
      } else {
        this.loadAllLeases();
      }
    });
  }

  viewActiveLease() {
    if (!this.activeLease) {
      return;
    }
    this.viewLeaseSummary(this.activeLease);
  }

  /** Open the editable lease form dialog. */
  editLease(lease: any, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    if (!lease || !lease.id) {
      alert('Cannot edit lease: lease ID not found');
      return;
    }
    const property = lease.property || this.property || { id: lease.propertyId };
    if (!property || !property.id) {
      alert('Cannot edit lease: property not found');
      return;
    }
    const dialogRef = this.dialog.open(ModifyLeaseComponent, {
      width: '920px',
      maxWidth: '100vw',
      maxHeight: '100vh',
      autoFocus: false,
      panelClass: 'lease-dialog-panel',
      data: { lease, property }
    });
    dialogRef.afterClosed().subscribe(() => {
      if (this.property) {
        this.refresh();
      } else {
        this.loadAllLeases();
      }
    });
  }

  /** Backward-compatible alias used by older templates / callers. */
  viewLease(lease: any, event?: Event) {
    if (lease && lease.status === 'EXPIRED') {
      this.viewLeaseSummary(lease, event);
    } else {
      this.editLease(lease, event);
    }
  }

  endLease(lease, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    if (!confirm('Are you sure you want to end this lease?')) {
      return;
    }
    const property = lease.property || { id: lease.propertyId };
    this.propertyService.endLease(property, lease).then(() => {
      if (this.property) {
        this.refresh();
      } else {
        this.loadAllLeases();
      }
    }).catch((error) => {
      console.error('Error ending lease:', error);
      let errorMessage = 'Failed to end lease. Please try again.';
      if (error && error.error && error.error.error) {
        errorMessage = error.error.error;
      } else if (error && error.message) {
        errorMessage = error.message;
      }
      alert(errorMessage);
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchText = filterValue.trim().toLowerCase();
    this.applyAllFilters();
  }

  onPropertyFilterChange(propertyId: string | null) {
    this.selectedPropertyId = propertyId;
    this.currentPage = 1; // Reset to first page when filter changes
    this.loadAllLeases();
  }

  onStatusFilterChange(status: string | null) {
    this.selectedStatus = status;
    // Status filter is client-side only for now
    this.applyAllFilters();
  }

  onDateFilterChange(filterType: string) {
    this.selectedDateFilter = filterType;
    if (filterType === 'thisMonth') {
      const now = new Date();
      this.customStartDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      this.customEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    } else if (filterType === 'lastMonth') {
      const now = new Date();
      this.customStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      this.customEndDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
    } else if (filterType === 'all') {
      this.customStartDate = '';
      this.customEndDate = '';
    }
    this.currentPage = 1; // Reset to first page when filter changes
    this.loadAllLeases();
  }

  onCustomDateChange() {
    if (this.customStartDate && this.customEndDate) {
      this.selectedDateFilter = 'custom';
    }
    this.currentPage = 1; // Reset to first page when filter changes
    this.loadAllLeases();
  }

  applyAllFilters() {
    // With server-side pagination, property and date filters are handled server-side.
    // Only apply status and search text filters client-side on the current page
    let filtered = [...this.allLeases];

    // Filter by status (client-side on current page only)
    if (this.selectedStatus) {
      filtered = filtered.filter(lease => lease.status === this.selectedStatus);
    }

    // Filter by search text (client-side on current page only)
    if (this.searchText) {
      filtered = filtered.filter(lease => {
        const searchLower = this.searchText.toLowerCase();
        const propertyName = lease.property ? 
          `${lease.property.addressLine1 || ''} ${lease.property.city || ''} ${lease.property.state || ''}`.toLowerCase() : '';
        const tenantName = `${lease.firstName || ''} ${lease.lastName || ''}`.toLowerCase();
        const status = lease.status ? lease.status.toLowerCase() : '';
        const monthlyCost = lease.monthlyCost ? String(lease.monthlyCost).toLowerCase() : '';
        
        return propertyName.includes(searchLower) ||
               tenantName.includes(searchLower) ||
               status.includes(searchLower) ||
               monthlyCost.includes(searchLower);
      });
    }

    this.filteredLeases = filtered;
    this.dataSource.data = this.filteredLeases; // Update data instead of creating new dataSource
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
    // Explicitly ensure paginator is NOT connected to dataSource for server-side pagination
    if (this.dataSource.paginator) {
      this.dataSource.paginator = null;
    }
  }

  clearFilters() {
    this.selectedPropertyId = null;
    this.selectedStatus = null;
    this.searchText = '';
    this.selectedDateFilter = 'all';
    this.customStartDate = '';
    this.customEndDate = '';
    this.currentPage = 1; // Reset to first page when clearing filters
    this.loadAllLeases();
  }

  close() {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    if (this.property && this.property.id) {
      this.navContext.goToPropertyHub(this.property.id);
      return;
    }
    this.navContext.goToDashboard();
  }
}
